<template>
  <feedback-section :progress-active="progressActive"
                    :button-description="lastUpdatedText"
                    class="assignment-type-section"
                    @save="save">
    <section class="assignment-type-section__sections-wrapper">
      <div class="post-form__section">
        <p class="post-form__section-title assignment-type-section__title">{{ $gettext('Change job category') }}</p>
        <sk-note v-if="isVatFreeCategory"
                 :text="note"
                 class="assignment-type-section__note" />
        <sk-select :description="categoryText"
                   :default-value="selectCategoryText"
                   :items-list="categories"
                   :ignore-categories="true"
                   :preselected-value="categoryId"
                   class="post-form__field subject__select post-form__field--half-width"
                   @csvaluechanged="setCategory" />
      </div>
    </section>
    <div v-if="showVatFreeBlock"
         class="vat-decision-reference">
      <sk-note v-if="isVatFreeCategory"
               :text="vatDecisionReferenceText" />
      <sk-input v-if="isVatDecisionReference"
                :description="vatDecisionReferenceDescription"
                :placeholder="vatDecisionReferenceDescription"
                :preselected-value="vatDecisionReference"
                :reg-exp="/[^0-9a-zA-ZÆØÅæøå\/:\-\\ ]/g"
                class="vat-decision-reference__input"
                @fieldvaluechanged="setVatDecisionReference" />
      <sk-checkbox v-if="isVatDecisionReference"
                   :preselected-value="acceptedVatFreePolicy"
                   :item-text="acceptedVatFreeText"
                   :validation-name="vatFreePolicyErrorName"
                   class="vat-decision-reference__checkbox"
                   @checkboxvaluechanged="setAcceptedVatFreePolicy" />
    </div>
  </feedback-section>
</template>

<script>
  import {mapMutations, mapState, mapGetters} from 'vuex';
  import helpers from '@/helpers';
  import FeedbackSection
    from '@/components/assignment_components/one_assignment/interactive_info/pt_feedback/FeedbackSection';

  export default {
    components: {
      'feedback-section': FeedbackSection
    },
    data() {
      return {
        progressActive: false
      };
    },
    computed: {
      ...mapGetters('PostingStore/SubjectStore', ['getCategoryData']),
      ...mapState('PostingStore/SubjectStore', {
        categories: (state) => state.categoriesList || [],
        categoryId: (state) => state.category.toString(),
        vatDecisionReference: (state) => state.vatDecisionReference,
        acceptedVatFreePolicy: (state) => state.acceptedVatFreePolicy,
        vatFreePolicyErrorName: (state) => state.errors.vatFreePolicy.name,
      }),
      ...mapState('OneAssignmentStore', {
        assignmentObj: ({storeJobObj}) => storeJobObj || {}
      }),
      ...mapState('FeedbackStore', {
        lastUpdatedData: ({lastUpdatedData}) => lastUpdatedData?.job_updated_assignment_type,
        lastUpdatedJobData: ({lastUpdatedData}) => lastUpdatedData?.job_updated
      }),
      acceptedVatFreeText() { return this.$gettext('I confirm that I have read and understood the information'); },
      categoryText() { return this.$gettext('Category'); },
      selectCategoryText() { return this.$gettext('Select a category'); },
      vatDecisionReferenceDescription() { return this.$gettext('Decision reference number'); },
      vatDecisionReferenceText() {
        const template = this.$gettext('If a category containing the text "with a decision that includes interpreting" is selected, the assignment may be invoiced without value-added tax (VAT). An important condition for this is that the reference number of the decision is provided and that the decision can be presented to %{domain} for possible verification at a later date.');
        return this.$gettextInterpolate(template, {domain: this.$getString('domainData', 'platformName')});
      },
      showVatFreeBlock() { return this.isVatFreeCategory || this.isVatDecisionReference; },
      isVatDecisionReference() { return this.getCategoryData?.vatDecisionReferenceNumberEnabled; },
      lastUpdatedText() {
        return helpers.feedbackInfo.lastUpdatedText({
          context: this,
          lastUpdatedSectionData: this.lastUpdatedData,
          lastUpdatedJobData: this.lastUpdatedJobData,
        });
      },
      jobId() {
        return this.$route.params.id;
      },
      statementId() {
        return this.assignmentObj.statementId;
      },
      isVatFreeCategory() { return this.getCategoryData.vatFree || false; },
      note() {
        return this.$gettext('By choosing an MVA-fri category, you confirm that this assignment is outside VAT law. If required, we may request your accounting department for documentation regarding this.');
      }
    },
    methods: {
      ...mapMutations('PostingStore/SubjectStore', [
        'setCategory',
        'setVatDecisionReference',
        'setAcceptedVatFreePolicy'
      ]),
      save() {
        this.progressActive = true;
        this.$store.dispatch('JobDecisionStore/updateJobStatement', {
          jobId: this.jobId,
          statementId: this.statementId
        })
          .then(() => {
            this.$store.dispatch('FeedbackStore/getLastUpdatedData', {
              jobId: this.jobId
            })
              .then(() => {
                this.progressActive = false;
              })
              .catch((error) => {
                this.progressActive = false;
                this.$store.commit('ModalStore/setModal', {
                  component: 'error-modal',
                  data: {
                    error
                  }
                });
              });
          })
          .catch(() => {
            this.progressActive = false;
            this.$store.commit('ModalStore/setModal', {
              component: 'error-modal'
            });
          });
      }
    },
    mounted() {
      if (this.assignmentObj.info.jobCategoryItem) {
        this.setCategory(this.assignmentObj.info.jobCategoryItem.id);
      }
      if (this.assignmentObj.info.vatDecisionReferenceNumber) {
        this.setVatDecisionReference(this.assignmentObj.info.vatDecisionReferenceNumber);
      }
    }
  };
</script>

<style scoped>
.assignment-type-section__sections-wrapper {
  margin: 0 -15px;
}

.assignment-type-section__radios-wrapper {
  display: flex;
  flex-wrap: wrap;
  margin-top: 5px;
}

.assignment-type-section__radio {
  width: auto;
}

.assignment-type-section__title {
  width: 100%;
}

.assignment-type-section__note {
  margin: 0 15px 10px;
}

.vat-decision-reference {
  width: 100%;
  margin: 10px 0 30px 0;
  padding: 8px;
  border-radius: 4px;
  background-color: var(--color-warning-200, #f0e8dd);
}

.vat-decision-reference__input {
  width: 60%;
  margin-bottom: 0;
  margin-left: 35px;
}

.vat-decision-reference__checkbox {
  margin-top: 16px;
  margin-left: 35px;
  font-size: 12px;
}
</style>

<style>
.vat-decision-reference__input > .sk-input__input-wrapper {
  background-color: #fff;
}
</style>
